/**
 * WcOrders — WooCommerce Orders List (mobile-first cards)
 * Single source of truth: WordPress/WooCommerce database
 * Full order management for store managers
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  wpApi,
  WcOrderSummary,
  WcOrderCounts,
  WcOrdersParams,
} from '../services/wordpressApi';
import {
  RefreshCw, Search, Package, AlertTriangle, Truck,
  ChevronRight, Filter, X,
} from 'lucide-react';
import '../styles/WcOrders.css';

// ── Status config ──────────────────────────────────────────────

interface StatusConfig {
  label: string;
  color: string;
  bg: string;
  emoji: string;
}

const STATUS_MAP: Record<string, StatusConfig> = {
  processing:  { label: 'Przetwarzane', color: '#f59e0b', bg: '#fef3c7', emoji: '🔄' },
  'on-hold':   { label: 'Wstrzymane',   color: '#ef4444', bg: '#fee2e2', emoji: '⏸️' },
  completed:   { label: 'Zrealizowane', color: '#10b981', bg: '#d1fae5', emoji: '✅' },
  pending:     { label: 'Oczekujące',   color: '#6b7280', bg: '#f3f4f6', emoji: '⏳' },
  cancelled:   { label: 'Anulowane',    color: '#6b7280', bg: '#f3f4f6', emoji: '❌' },
  refunded:    { label: 'Zwrócone',     color: '#8b5cf6', bg: '#ede9fe', emoji: '↩️' },
  failed:      { label: 'Nieudane',     color: '#dc2626', bg: '#fee2e2', emoji: '⛔' },
};

const getStatus = (s: string): StatusConfig =>
  STATUS_MAP[s] || { label: s, color: '#6b7280', bg: '#f3f4f6', emoji: '❓' };

// Filter tabs for quick access (order matters)
const STATUS_FILTERS = [
  { key: '',           label: 'Wszystkie' },
  { key: 'processing', label: 'Przetwarzane' },
  { key: 'on-hold',    label: 'Wstrzymane' },
  { key: 'completed',  label: 'Zrealizowane' },
  { key: 'pending',    label: 'Oczekujące' },
  { key: 'cancelled',  label: 'Anulowane' },
  { key: 'refunded',   label: 'Zwrócone' },
  { key: 'failed',     label: 'Nieudane' },
];

// ── Format helpers ─────────────────────────────────────────────

function formatDate(dateStr: string): string {
  const d = new Date(dateStr.replace(' ', 'T'));
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()} ${hours}:${mins}`;
}

function formatMoney(amount: string, currency = 'PLN'): string {
  const n = parseFloat(amount);
  return `${n.toFixed(2)} ${currency}`;
}

function getPaymentIcon(method: string): string {
  if (method.includes('cod') || method.includes('pobranie')) return '💰';
  if (method.includes('card') || method.includes('kart')) return '💳';
  if (method.includes('blik')) return '📱';
  if (method.includes('transfer') || method.includes('przelew')) return '🏦';
  if (method.includes('paynow')) return '💳';
  return '💲';
}

// ── Component ──────────────────────────────────────────────────

export function WcOrders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [orders, setOrders] = useState<WcOrderSummary[]>([]);
  const [counts, setCounts] = useState<WcOrderCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters from URL
  const activeStatus = searchParams.get('status') || '';
  const searchQuery = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(searchQuery);

  // ── Data fetching ──
  const fetchOrders = useCallback(async (params: WcOrdersParams, append = false) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);
      setError(null);

      const data = await wpApi.getWcOrders({
        per_page: 20,
        ...params,
      });

      if (append) {
        setOrders(prev => [...prev, ...data.orders]);
      } else {
        setOrders(data.orders);
      }
      setTotal(data.total);
      setPages(data.pages);
      setCurrentPage(data.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd pobierania zamówień');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const data = await wpApi.getWcOrderCounts();
      setCounts(data);
    } catch {
      // Non-critical, silently ignore
    }
  }, []);

  // Initial load + filter changes
  useEffect(() => {
    const params: WcOrdersParams = { page: 1 };
    if (activeStatus) params.status = activeStatus;
    if (searchQuery) params.search = searchQuery;
    fetchOrders(params);
    fetchCounts();
  }, [activeStatus, searchQuery, fetchOrders, fetchCounts]);

  // ── Handlers ──
  const handleStatusFilter = (status: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (status) newParams.set('status', status);
    else newParams.delete('status');
    setSearchParams(newParams);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchInput.trim()) newParams.set('q', searchInput.trim());
    else newParams.delete('q');
    setSearchParams(newParams);
  };

  const clearSearch = () => {
    setSearchInput('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('q');
    setSearchParams(newParams);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    const params: WcOrdersParams = { page: 1 };
    if (activeStatus) params.status = activeStatus;
    if (searchQuery) params.search = searchQuery;
    fetchOrders(params);
    fetchCounts();
  };

  const handleLoadMore = () => {
    if (currentPage >= pages) return;
    const params: WcOrdersParams = { page: currentPage + 1 };
    if (activeStatus) params.status = activeStatus;
    if (searchQuery) params.search = searchQuery;
    fetchOrders(params, true);
  };

  const handleOrderClick = (orderId: number) => {
    navigate(`/wc-orders/${orderId}`);
  };

  // ── Render ──
  if (loading && orders.length === 0) {
    return (
      <div className="wc-orders-loading">
        <div className="pulse-dot" />
        <span>Ładowanie zamówień…</span>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="wc-orders-error">
        <AlertTriangle size={40} />
        <p>{error}</p>
        <button className="btn-refresh" onClick={handleRefresh}>Spróbuj ponownie</button>
      </div>
    );
  }

  const activeCount = activeStatus && counts ? counts[activeStatus] : counts?.all;

  return (
    <div className="wc-orders-page">
      {/* ── Header ── */}
      <div className="wc-orders-header">
        <div className="wc-orders-header-top">
          <div>
            <h1>Zamówienia</h1>
            <span className="wc-orders-subtitle">
              {activeCount != null ? `${activeCount} ` : ''}{activeStatus ? getStatus(activeStatus).label.toLowerCase() : 'łącznie'}
            </span>
          </div>
          <button
            className={`btn-icon-refresh ${refreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            title="Odśwież"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* ── Status Count Chips ── */}
      {counts && (
        <div className="wc-orders-counts">
          {counts.processing > 0 && (
            <div className="count-chip processing">
              <span className="count-chip-num">{counts.processing}</span>
              <span className="count-chip-label">przetw.</span>
            </div>
          )}
          {counts['on-hold'] > 0 && (
            <div className="count-chip on-hold">
              <span className="count-chip-num">{counts['on-hold']}</span>
              <span className="count-chip-label">wstrzym.</span>
            </div>
          )}
          {counts.pending > 0 && (
            <div className="count-chip pending">
              <span className="count-chip-num">{counts.pending}</span>
              <span className="count-chip-label">oczek.</span>
            </div>
          )}
        </div>
      )}

      {/* ── Status Filter Pills ── */}
      <div className="wc-orders-filters">
        <div className="filter-pills">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              className={`filter-pill ${activeStatus === f.key ? 'active' : ''}`}
              onClick={() => handleStatusFilter(f.key)}
            >
              {f.key && counts ? (
                <>
                  <span>{f.label}</span>
                  <span className="pill-count">{counts[f.key] ?? 0}</span>
                </>
              ) : (
                <span>{f.label}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Search ── */}
      <form className="wc-orders-search" onSubmit={handleSearch}>
        <div className="search-input-wrap">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Szukaj: numer, nazwisko, email…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button type="button" className="search-clear" onClick={clearSearch}>
              <X size={14} />
            </button>
          )}
        </div>
        <button type="submit" className="search-btn">
          <Filter size={16} />
        </button>
      </form>

      {/* ── Active search indicator ── */}
      {searchQuery && (
        <div className="wc-search-active">
          <span>Wyniki dla: <strong>"{searchQuery}"</strong></span>
          <button onClick={clearSearch}>
            <X size={14} /> Wyczyść
          </button>
        </div>
      )}

      {/* ── Orders List ── */}
      <div className="wc-orders-list">
        {orders.length === 0 ? (
          <div className="wc-orders-empty">
            <Package size={48} />
            <p>Brak zamówień{activeStatus ? ` ze statusem "${getStatus(activeStatus).label}"` : ''}</p>
          </div>
        ) : (
          orders.map(order => {
            const status = getStatus(order.status);
            return (
              <div
                key={order.id}
                className="wc-order-card"
                onClick={() => handleOrderClick(order.id)}
              >
                {/* Row 1: Order # + Status */}
                <div className="order-card-top">
                  <span className="order-card-number">#{order.number}</span>
                  <span
                    className="order-card-status"
                    style={{ color: status.color, background: status.bg }}
                  >
                    {status.emoji} {status.label}
                  </span>
                </div>

                {/* Row 2: Customer + Amount */}
                <div className="order-card-main">
                  <div className="order-card-customer">
                    <span className="order-card-name">{order.customer_name}</span>
                    {order.customer_city && (
                      <span className="order-card-city">{order.customer_city}</span>
                    )}
                  </div>
                  <div className="order-card-amount">
                    {formatMoney(order.total, order.currency)}
                  </div>
                </div>

                {/* Row 3: Meta info */}
                <div className="order-card-meta">
                  <span className="meta-tag">
                    <Package size={12} /> {order.items_count} szt.
                  </span>
                  <span className="meta-tag">
                    {getPaymentIcon(order.payment_method)} {order.payment_method_title}
                  </span>
                  {order.shipping_method && (
                    <span className="meta-tag">
                      <Truck size={12} /> {order.shipping_method}
                    </span>
                  )}
                </div>

                {/* Row 4: Indicators */}
                <div className="order-card-bottom">
                  <span className="order-card-date">{formatDate(order.date_created)}</span>
                  <div className="order-card-indicators">
                    {order.has_complaint && (
                      <span className="indicator complaint" title={order.complaint_text || 'Reklamacja'}>
                        ⚠️
                      </span>
                    )}
                    {order.tracking_number && (
                      <span className="indicator tracking" title={`Tracking: ${order.tracking_status}`}>
                        📦
                      </span>
                    )}
                    {order.supplier_order_id && (
                      <span className="indicator supplier" title={`Dostawca: ${order.supplier_order_id}`}>
                        🏭
                      </span>
                    )}
                    {order.customer_note && (
                      <span className="indicator note" title="Notatka klienta">
                        💬
                      </span>
                    )}
                    <ChevronRight size={16} className="order-card-arrow" />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Load More ── */}
      {currentPage < pages && (
        <div className="wc-orders-loadmore">
          <button
            className="btn-load-more"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <><div className="pulse-dot small" /> Ładowanie…</>
            ) : (
              <>Załaduj więcej ({orders.length} z {total})</>
            )}
          </button>
        </div>
      )}

      {/* ── Error toast ── */}
      {error && orders.length > 0 && (
        <div className="wc-orders-toast">
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
